<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { i18n, setLocale, t } from '@/i18n';
import { detectOffer, LANGUAGE_OFFERS, readStoredLocale, type Locale } from '@/i18n/locale';
import { useSessionStore } from '@/stores/session';

/*
 * The shell. It reads the session store so the header can say who you are — permitted for a
 * view and not for anything under components/, per docs/ARCHITECTURE.md.
 *
 * The session itself is resolved by the router guard, not here: this only renders whatever the
 * store already knows.
 */
const session = useSessionStore();

/*
 * One screen — local play — is handed to a room full of people, and the header is the reason it
 * has to be: Host and My games are one tap from the settings that screen exists to keep them
 * away from. Opt-out rather than opt-in, so a new route keeps the shell without saying anything.
 *
 * The footer stays either way. It is a disclaimer and an address to write to, not navigation.
 */
const route = useRoute();

/*
 * Sign-in comes back to the screen it was asked for from.
 *
 * Without this the sign-in screen has nothing to go on and falls back to "host a game" — which
 * is how somebody who arrived on a join link ended up being asked to create one. The router's
 * own guard has always sent a `redirect`; this link is the one that did not.
 *
 * Encoded, because a fullPath is allowed to contain the `&` and `#` that would otherwise end
 * the query parameter early. `safeRedirect` is what decides whether to honour it.
 */
const signInLink = computed(() => `/sign-in?redirect=${encodeURIComponent(route.fullPath)}`);

/*
 * The brand mark, as a URL rather than a literal `src`.
 *
 * A literal would be rewritten by the Vue compiler's asset handling and inlined as a data URI,
 * because it is under Vite's 4 KB threshold. That is not wrong, but it puts a second copy of the
 * mark in the bundle every visitor downloads — when the browser has already fetched this exact
 * file for the tab icon and will serve it from cache. Binding keeps it a plain request.
 */
const MARK = '/favicon.svg';

/*
 * Signing out asks first — but only a guest.
 *
 * For an account holder it is reversible: sign in again and the pools come back. For a guest the
 * session *is* the record, so this one tap destroys the only route to every pool they host or
 * play in, and it was the top-right corner of the screen — the hardest pixel to hit by accident
 * and the easiest to hit on purpose while reaching for something else.
 */
/*
 * The offer of another language, once, in the language it is offering.
 *
 * `bootLocale` ignores the browser's languages deliberately — a first screen in a language nobody
 * chose is worse than a predictable one — and the cost was an Afrikaans reader whose only route
 * to Afrikaans was a 12px footer link at 3.26:1, three screens deep behind a name form. This says
 * the option exists and still changes nothing until somebody chooses.
 *
 * **Dismissing stores the language they are already in.** That is what silences it, so there is
 * no second flag and no new browser-storage key to disclose on the privacy page.
 */
const stored = ref(readStoredLocale());
const offer = computed(() => detectOffer(i18n.global.locale.value as Locale, stored.value));

async function chooseLanguage(locale: Locale): Promise<void> {
  await setLocale(locale);
  stored.value = locale;
}

const confirmingSignOut = ref(false);
const isGuest = computed(() => session.subject?.kind === 'GUEST');

function onSignOut(): void {
  if (isGuest.value && !confirmingSignOut.value) {
    confirmingSignOut.value = true;
    return;
  }
  void signOut();
}

async function signOut(): Promise<void> {
  confirmingSignOut.value = false;
  try {
    await session.signOut();
  } catch {
    // A failed sign-out leaves the session exactly as it was, which the header already reflects.
    // Letting it reject would take the whole shell down with it.
  }
}
</script>

<template>
  <div class="flex min-h-dvh flex-col bg-page text-ink font-display">
    <!--
      A column that fills the viewport, so the footer starts where it ends up.

      As a plain block this sat directly under the header on the first paint and fell the height
      of the page the moment the view rendered into the empty `<main>` below — the larger of the
      two elements Cloudflare's RUM named in a CLS of 1.0 on the landing page. `flex-1` on the
      main column is what holds that space open while the view is still on its way.
    -->
    <!--
      The first thing Tab reaches, on every screen.

      A game screen's board is a hundred cells above the share panel and the ledger in DOM order;
      the roving tabindex in `SquaresGrid` is what makes that one tab stop, and this is the other
      half — a way past the chrome that costs nothing to have. `sr-only` until focused, which is
      the whole pattern: invisible to a pointer, unmissable to a keyboard.
    -->
    <a
      href="#main"
      class="sr-only rounded bg-accent px-4 py-2 font-medium text-on-accent focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-20"
    >
      {{ t('app.skipToContent') }}
    </a>

    <header v-if="route.meta.chrome !== false" class="border-b border-line bg-surface">
      <div
        class="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3"
      >
        <!--
          Wraps, and does *not* refuse to break: at a 200% root font on a wider system font than
          this was written on — CI's container resolves `system-ui` to DejaVu Sans, which is about
          a tenth wider than SF Pro — "Rugby Squares" plus its mark measured 310px inside 288px of
          content and took the page off the side of a 320px screen. Two words are allowed to
          become two lines.
        -->
        <RouterLink to="/" class="flex min-w-0 items-center gap-2 font-semibold">
          <!--
            The same file the browser tab shows, deliberately. Two copies of a mark drift, and the
            pair that drifts most visibly is the tab and the header — they are on screen together.
            It is generated by scripts/brand.mjs like the rest of the artwork, and
            test/unit/brand-assets.test.ts asserts the committed file still matches.

            `alt` is empty because the wordmark is the next node along: a described image here
            reads as "Rugby Squares Rugby Squares".
          -->
          <img :src="MARK" alt="" width="24" height="24" class="h-6 w-6 rounded-sm" />
          {{ t('app.name') }}
        </RouterLink>
        <!--
          Wraps, and every item refuses to break inside itself.
          
          It was one unwrapping line of five text targets plus the wordmark, with **no** slack at
          390px: 394px of content in a 390px viewport at 125% root text, 466px at 150%, 619px at
          200% — and at 320px "Join" overlapped the wordmark while "Sign out" ended past the edge.
          `whitespace-nowrap` is the other half: without it the boxes stayed on one line and the
          *words* broke instead, which is how "My games" came to be read in a 43px column.
          test/e2e/header-reflow.spec.ts measures the page rather than the nav.

          **`min-h-6 min-w-6` on each item is SC 2.5.8**, AA's 24px floor. These were 20px tall —
          a line of text is a line of text — and the two that cleared it did so only because
          their words happened to wrap. `inline-flex`, because an inline box ignores a height.
        -->
        <nav class="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-sm">
          <RouterLink
            to="/join"
            class="inline-flex min-h-6 min-w-6 items-center justify-center whitespace-nowrap hover:underline"
          >
            {{ t('app.nav.join') }}
          </RouterLink>
          <RouterLink
            to="/host/new"
            class="inline-flex min-h-6 min-w-6 items-center justify-center whitespace-nowrap hover:underline"
          >
            {{ t('app.nav.host') }}
          </RouterLink>
          <!--
            Rendered only once the session has actually been resolved. Flashing "Sign in" at
            somebody who turns out to be signed in is worse than a moment of nothing.
          -->
          <template v-if="session.loaded">
            <span
              v-if="session.isSignedIn"
              data-session
              class="flex flex-wrap items-center justify-end gap-x-3 gap-y-1"
            >
              <!-- Only with a session: without one there is nothing to list, and the link would
                   do no more than bounce off the guard. A guest belongs here as much as a host —
                   they have games too. -->
              <RouterLink
                to="/games"
                class="inline-flex min-h-6 min-w-6 items-center justify-center whitespace-nowrap hover:underline"
              >
                {{ t('app.nav.myGames') }}
              </RouterLink>
              <!--
                The name is the way to change it: a person looking for "where do I fix this?"
                looks at the thing that is wrong. `session.displayName` falls back to the email's
                local part, because the backend stores nothing until somebody chooses.

                **Below `sm:` it is not here.** Five text targets do not fit in 200px, and this is
                the one with somewhere else to be — the footer's Settings link reaches the same
                screen on every page, and a display name is not navigation.
              -->
              <RouterLink
                to="/settings"
                class="hidden inline-flex min-h-6 min-w-6 items-center justify-center whitespace-nowrap text-ink-muted hover:underline sm:inline-flex"
              >
                {{ session.displayName }}
              </RouterLink>
              <button
                type="button"
                class="inline-flex min-h-6 min-w-6 items-center justify-center whitespace-nowrap hover:underline"
                @click="onSignOut"
              >
                {{ t('app.nav.signOut') }}
              </button>
            </span>
            <RouterLink
              v-else
              data-session
              :to="signInLink"
              class="inline-flex min-h-6 min-w-6 items-center justify-center whitespace-nowrap hover:underline"
            >
              {{ t('app.nav.signIn') }}
            </RouterLink>
          </template>
          <!--
            The space that link will need, held open while we find out whether it is wanted.

            Rendering nothing here is what moved the nav sideways when /me landed — the second
            element in that CLS of 1.0. `invisible` is `visibility: hidden`, which keeps the box
            and empties it: nothing is flashed at somebody who turns out to be signed in, and
            there is nothing to read out or to tab onto either, so `aria-hidden` is belt to that
            braces rather than the mechanism.

            It reserves the signed-*out* width, because that is who most people arriving here
            are. Somebody with a session still gets a small reflow when their name and its two
            links replace it, and the honest reason that is left alone is that the alternative
            is remembering across visits that this browser had a session — a per-visitor flag,
            which is a privacy decision rather than a layout one.
          -->
          <span v-else data-session-pending aria-hidden="true" class="invisible">
            {{ t('app.nav.signIn') }}
          </span>
        </nav>
      </div>

      <!--
        The language offer, in the same place and for the same reason as the bar below it: a
        sentence needs a line of its own at 390px. It is out of the header's own flow, so it
        cannot make the nav's reflow any tighter.
      -->
      <div v-if="offer" class="border-t border-line bg-fill/40">
        <div class="mx-auto flex max-w-4xl flex-wrap items-center gap-3 px-4 py-3 text-sm">
          <p lang="{{ offer }}">{{ LANGUAGE_OFFERS[offer].line }}</p>
          <button
            type="button"
            :lang="offer"
            class="min-h-11 rounded bg-accent px-3 py-1.5 font-medium text-on-accent"
            @click="chooseLanguage(offer)"
          >
            {{ LANGUAGE_OFFERS[offer].action }}
          </button>
          <button
            type="button"
            class="text-target underline text-ink-muted hover:text-ink"
            @click="chooseLanguage(i18n.global.locale.value as Locale)"
          >
            {{ t('app.noThanks') }}
          </button>
        </div>
      </div>

      <!--
        A full-width bar under the header rather than two more buttons in it: the nav has no room
        for a sentence at 390px, and this one has to be read rather than glanced at. Only a guest
        ever sees it — see `onSignOut`.
      -->
      <div v-if="confirmingSignOut" class="border-t border-line bg-fill/40">
        <div class="mx-auto flex max-w-4xl flex-wrap items-center gap-3 px-4 py-3 text-sm">
          <p class="font-medium">{{ t('app.nav.confirmSignOut') }}</p>
          <button
            type="button"
            class="rounded bg-accent-strong px-3 py-1.5 font-medium text-on-accent"
            @click="signOut"
          >
            {{ t('app.nav.yesSignOut') }}
          </button>
          <button
            type="button"
            class="rounded border border-accent px-3 py-1.5 font-medium hover:bg-fill"
            @click="confirmingSignOut = false"
          >
            {{ t('app.nav.staySignedIn') }}
          </button>
        </div>
      </div>
    </header>

    <!-- `w-full` because a flex item is sized by its content, not by the line it sits on: without
         it the auto margins have nothing to centre and the column collapses to the width of the
         longest paragraph. -->
    <main id="main" class="mx-auto w-full max-w-4xl flex-1 px-4 py-8">
      <RouterView />
    </main>

    <footer class="mx-auto w-full max-w-4xl px-4 pb-8 text-xs text-ink-muted">
      <p>{{ t('app.footer.disclaimer') }}</p>
      <!--
        The one way in for somebody with no session — the language choice needs no account, so
        it cannot live only inside the signed-in half of the header. Gone on a handed-over
        screen: the footer survives there because it is a disclaimer, and this is navigation.
      -->
      <!--
        Wraps for the same reason the nav does, and found by the same test: at a 200% root font
        these four links measured 395px in a 390px viewport, which is the header's bug in the
        other corner of the page. `gap-y-1` so two rows of links do not touch.
      -->
      <p v-if="route.meta.chrome !== false" class="mt-1 flex flex-wrap gap-x-3 gap-y-1">
        <!--
          First, because it is the one a first-time visitor is looking for; settings and privacy
          are lookups. Gated with them for the same reason they are: this is navigation.
        -->
        <RouterLink to="/how-to-play" class="text-target underline hover:text-ink">
          {{ t('app.footer.howToPlay') }}
        </RouterLink>
        <RouterLink to="/settings" class="text-target underline hover:text-ink">
          {{ t('app.nav.settings') }}
        </RouterLink>
        <!--
          Gated with settings rather than sitting next to the disclaimer, and for the same reason:
          this is navigation. On a handed-over screen the phone is being passed around a room, and
          a link out of local play is a way to lose somebody's game.
        -->
        <RouterLink to="/privacy" class="text-target underline hover:text-ink">
          {{ t('app.footer.privacy') }}
        </RouterLink>
        <!--
          This used to be a `mailto:` in its own paragraph outside the gate, which is how the
          address came to be printed on every screen. A link is navigation, so it joins the other
          two behind the chrome gate — and on a handed-over local-play screen there is then no way
          to reach us, which is the same trade settings and privacy already make.
        -->
        <RouterLink to="/contact" class="text-target underline hover:text-ink">
          {{ t('app.footer.contact') }}
        </RouterLink>
      </p>
    </footer>
  </div>
</template>
